<template>
    <div id="mail-integration">
        <div class="email-service-status">
            <div class="service-card">
                <div class="service-header">
                    <div class="service-title">{{ $t('mail.mailProviderServiceTitle') }}</div>
                    <div class="service-status" :class="{ 'status-active': domainIsCreated }">
                        {{ domainIsCreated ? $t('mail.mailProviderConnected') : $t('mail.mailProviderNotConnected') }}
                    </div>
                </div>

                <div class="service-content">
                    <div class="provider-logo">
                        <img :src="logoUrl" :alt="$t('mail.mailProviderLogoAlt')" />
                    </div>
                    <div class="provider-info">
                        <div class="provider-name">{{ $t('mail.mailActivateYourDomainTitle') }}</div>
                        <div class="domain-name">{{ emailDomain }}</div>
                    </div>
                    <div class="action-area">
                        <SupsisButton v-if="!domainIsCreated && emailBtnEnabled" primary @click="createDomain">
                            {{ $t('mail.mailConnect') }}
                        </SupsisButton>
                        <SupsisButton v-if="!emailBtnEnabled" :disabled="true">{{ $t('mail.mailLoadingShort') }}</SupsisButton>
                        <div v-if="domainIsCreated && emailBtnEnabled" class="domain-connected">
                            <span class="material-icons check-icon">check_circle</span>
                            <span>{{ $t('mail.mailVerifiedLabel') }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mail-dashboard" v-if="domainIsCreated">
            <div class="dashboard-header">
                <div class="dashboard-title">Mail Yönetimi</div>
                <div class="dashboard-tabs">
                    <div class="tab-count">{{ allowedMailAddresses.length }}</div>
                </div>
            </div>

            <div class="mail-section">
                <div class="section-header">
                    <div class="section-title">
                        <span class="material-icons section-icon">person</span>
                        {{ $t('mail.mailUserEmailAddressesTitle') }}
                    </div>
                    <SupsisButton primary @click="$feature.beforeCreateUserMail(showModel)" v-if="domainIsCreated">
                        <span class="material-icons">add</span>
                        {{ $t('mail.mailAddNewAddress') }}
                    </SupsisButton>
                </div>

                <div class="mail-groups">
                    <template v-if="Object.keys(groupedUserMails).length === 0">
                        <div class="empty-state">
                            <span class="material-icons empty-icon">email</span>
                            <div class="empty-text">{{ $t('mail.mailNoAddressesFound') }}</div>
                            <div class="empty-description">
                                {{ $t('mail.mailNoAddressesHint') }}
                            </div>
                        </div>
                    </template>

                    <div
                        class="mail-group-container"
                        v-for="(group, groupId) in groupedUserMails"
                        :key="'user-' + groupId"
                    >
                        <div class="mail-group" :class="{ 'deleted-group': group.isDeleted }">
                            <div class="group-header">
                                <div class="group-title">
                                    <span class="material-icons user-icon">{{ group.isDeleted ? 'person_off' : 'account_circle' }}</span>
                                    {{ typeof group === "object" ? group.name : $t('mail.mailUnknownUser') }}
                                </div>
                                <div class="group-badges">
                                    <span class="badge mail-count">{{ $t('mail.mailAddressCountLabel', { count: (group.mails || []).length }) }}</span>
                                </div>
                            </div>

                            <div class="mail-items">
                                <div class="mail-item" v-for="mail in group.mails || []" :key="mail._id">
                                    <div class="mail-icon">
                                        <MailIcon />
                                    </div>
                                    <div class="mail-info" @click="mailDetail(false, mail._id)">
                                        <div class="mail-address">{{ mail.mail }}</div>
                                        <div class="mail-meta">
                                            <span class="mail-status" :class="{ verified: mail.isVerified }">
                                                {{ mail.isVerified ? $t('mail.mailVerifiedShort') : $t('mail.mailUnverifiedShort') }}
                                            </span>
                                            <span class="mail-type">{{
                                                mail.isForward ? $t('mail.mailTypeForward') : $t('mail.mailTypeStandard')
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="mail-actions">
                                        <div class="delete-button" @click.stop="deleteMailAddress(mail)">
                                            <span class="material-icons">delete</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mail-section">
                <div class="section-header">
                    <div class="section-title">
                        <span class="material-icons section-icon">business</span>
                        {{ $t('mail.mailDepartmentAddressesTitle') }}
                    </div>
                    <SupsisButton
                        primary
                        @click="isAllowCreateDepartment() && showModel(true)"
                        :disabled="!isAllowCreateDepartment()"
                        v-if="domainIsCreated"
                    >
                        <span class="material-icons">add</span>
                        {{ $t('mail.mailAddNewAddress') }}
                    </SupsisButton>
                </div>

                <div class="mail-groups">
                    <template v-if="Object.keys(groupedDepartmentMails).length === 0">
                        <div class="empty-state">
                            <span class="material-icons empty-icon">business_center</span>
                            <div class="empty-text">{{ $t('mail.mailNoAddressesFound') }}</div>
                            <div class="empty-description">
                                {{ $t('mail.mailNoAddressesHint') }}
                            </div>
                        </div>
                    </template>

                    <div
                        class="mail-group-container"
                        v-for="(group, groupId) in groupedDepartmentMails"
                        :key="'dept-' + groupId"
                    >
                        <div class="mail-group" :class="{ 'deleted-group': group.isDeleted }">
                            <div class="group-header">
                                <div class="group-title">
                                    <span class="material-icons department-icon">{{ group.isDeleted ? 'domain_disabled' : 'apartment' }}</span>
                                    {{ group.name }}
                                </div>
                                <div class="group-badges">
                                    <span class="badge mail-count">{{ $t('mail.mailAddressCountLabel', { count: (group.mails || []).length }) }}</span>
                                </div>
                            </div>

                            <div class="mail-items">
                                <div class="mail-item" v-for="mail in group.mails || []" :key="mail._id">
                                    <div class="mail-icon">
                                        <MailIcon />
                                    </div>
                                    <div class="mail-info" @click="mailDetail(true, mail)">
                                        <div class="mail-address">{{ mail.mail }}</div>
                                        <div class="mail-meta">
                                            <span class="mail-status" :class="{ verified: mail.isVerified }">
                                                {{ mail.isVerified ? $t('mail.mailVerifiedShort') : $t('mail.mailUnverifiedShort') }}
                                            </span>
                                            <span class="mail-type">{{
                                                mail.isForward ? $t('mail.mailTypeForward') : $t('mail.mailTypeStandard')
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="mail-actions">
                                        <div class="delete-button" @click.stop="deleteMailAddress(mail)">
                                            <span class="material-icons">delete</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="setup-required" v-if="!domainIsCreated">
            <div class="setup-content">
                <span class="material-icons setup-icon">email</span>
                <div class="setup-title">{{ $t('mail.mailSetupRequiredTitle') }}</div>
                <div class="setup-description">
                    {{ $t('mail.mailSetupRequiredDesc') }}
                </div>
            </div>
        </div>

        <Modal2
            class-name="mail-create-modal"
            :title="isNewDepartment ? $t('mail.mailModalCreateDeptTitle') : $t('mail.mailModalCreateUserTitle')"
            :showFooter="false"
            :showTitle="false"
            @cancel="handleCancel()"
            v-if="isNewUserMail || isNewDepartment"
        >
            <div class="modal-body">
                <div class="modal-custom-header">
                    <div class="header-icon">
                        <span class="material-icons">{{ isNewDepartment ? "business" : "person" }}</span>
                    </div>
                    <h3 class="header-title">
                        {{ isNewDepartment ? $t('mail.mailModalCreateDeptTitle') : $t('mail.mailModalCreateUserTitle') }}
                    </h3>
                </div>

                <div class="modal-custom-content">
                    <div class="form-item" v-if="!!isNewDepartment">
                        <label for="dept-select">{{ $t('mail.mailSelectDepartmentLabel') }}</label>
                        <a-select id="dept-select" v-model="selectedDepartmentId" style="width: 100%">
                            <a-select-option
                                :value="subject._id"
                                v-for="subject in getSelectDepartmentList()"
                                :key="subject._id"
                            >
                                {{ subject.languageContent.tr.name }}
                            </a-select-option>
                        </a-select>
                    </div>

                    <div class="form-item mail-field-container">
                        <label for="mail-name">{{ $t('mail.senderTableMailAddress') }}</label>
                        <div class="mail-input-container" :class="{ 'input-error': isMailError }">
                            <a-input
                                id="mail-name"
                                class="mail-name-input"
                                :aria-disabled="Object.keys(groupedUserMails).length !== 0 && !isNewDepartment"
                                v-model="emailName"
                                :placeholder="$t('mail.mailLocalPartPlaceholder')"
                                @input="checkMailValidity"
                            />
                            <div class="mail-domain">@{{ emailDomain }}</div>
                        </div>
                        <div class="input-error-message" v-if="isMailError">
                            {{ mailErrorMessage }}
                        </div>
                        <div class="input-helper" v-if="emailName.length > 0 && !isMailError">
                            <span class="full-mail">{{ emailName }}@{{ emailDomain }}</span>
                        </div>
                    </div>

                    <div class="form-notice">
                        <span class="material-icons notice-icon">info</span>
                        <div class="notice-text">
                            {{ $t('mail.mailAddressRulesNotice') }}
                        </div>
                    </div>
                </div>

                <div class="modal-custom-footer">
                    <SupsisButton class="cancel-btn" @click="handleCancel">{{ $t('mail.cancel') }}</SupsisButton>
                    <SupsisButton
                        class="create-btn"
                        primary
                        @click="!!isNewDepartment ? createDepartment() : createUserEmail()"
                        :disabled="
                            !domainIsCreated ||
                            !emailBtnEnabled ||
                            isMailError ||
                            isMailExists ||
                            emailName.length === 0
                        "
                    >
                        {{ $t('mail.mailCreate') }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { log2 } from "../../../utils/log2";
import GoogleIcon from "./icons/GoogleIcon.vue";
import MailIcon from "./icons/MailIcon.vue";
import MicrosoftIcon from "./icons/MicrosoftIcon.vue";
import { sendSocketMessage, socketMessageStream } from "../../../utils/socketStream";
import Modal2 from "../../../components/common/Modal2.vue";
import ImageMessage from "../../../components/popup-chat/messages/ImageMessage.vue";
import CONFIG from "../../../config/config";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";
import WhatsappIcon from "../Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import NewW2Channel from "../WhatsappV2/NewW2Channel.vue";
import { DEFAULT_ORGANIZATION_LOGO_URL } from "../../../utils/staticDatas";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import SupsisButton from "../Settings/components/SupsisButton.vue";

const log = log2("MailIntegration");
export default {
    name: "MailIntegration",
    components: {
        NewW2Channel,
        WhatsappIcon,
        MailIcon,
        GoogleIcon,
        MicrosoftIcon,
        Modal2,
        ImageMessage,
        AppBreadCrumb,
        SupsisButton,
    },
    props: {
        visible: {},
    },
    data() {
        return {
            logoUrl: DEFAULT_ORGANIZATION_LOGO_URL,
            emailBtnEnabled: true,
            emailName: "",
            stepNumber: 1,
            selectedDepartmentId: null,
            isMailError: false,
            mailErrorMessage: "",
            isMailExists: false,
        };
    },
    computed: {
        ...mapState({
            subjects: (state) => state.subject.subjects,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            domainIsCreated: (state) => state.mail.domainIsCreated,
            isNewDomain: (state) => state.mail.isNewDomain,
            isNewDepartment: (state) => state.mail.isNewDepartment,
            verificationToken: (state) => state.mail.verificationToken,
            allMailAddresses: (state) => state.mail.allMailAddresses,
            isNewUserMail: (state) => state.mail.isNewUserMail,
            emailDomain: (state) =>
                state.site.site.alias === "supsis"
                    ? CONFIG.EMAIL_DOMAIN
                    : `${state.site.site.alias}.${CONFIG.EMAIL_DOMAIN}`,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
            allowedMailAddresses: "mail/allowedMailAddresses",
        }),
        isMailUpdatePermission() {
            return this.checkPermission(PERMISSION_TYPES["mail.update"].tr.name);
        },
        isMailDeletePermission() {
            return this.checkPermission(PERMISSION_TYPES["mail.delete"].tr.name);
        },
        groupedUserMails() {
            const userMails = this.allowedMailAddresses
                .filter((mail) => !mail.isSenderMail || !mail.isForward)
                .filter((mail) => mail.type === "user");
            const deleted = {};
            const active = {};

            userMails.forEach((mail) => {
                if (typeof mail.user === "string" || !mail.user) {
                    if (!deleted["SilinenKullanici"]) {
                        deleted["SilinenKullanici"] = {
                            name: this.$t("mail.mailGroupedDeletedUserName"),
                            isDeleted: true,
                            mails: [],
                        };
                    }
                    deleted["SilinenKullanici"].mails.push(mail);
                } else {
                    const userId = mail.user._id;
                    const userName = mail.user.name || this.$t("mail.mailUnknownUser");

                    if (!active[userId]) {
                        active[userId] = {
                            name: userName,
                            mails: [],
                        };
                    }
                    active[userId].mails.push(mail);
                }
            });

            return Object.assign({}, deleted, active);
        },
        groupedDepartmentMails() {
            const departmentMails = this.allowedMailAddresses
                .filter((mail) => !mail.isSenderMail || !mail.isForward)
                .filter((mail) => mail.type === "department");
            const deleted = {};
            const active = {};

            departmentMails.forEach((mail) => {
                if (!mail.department || typeof mail.department === "string") {
                    if (!deleted["SilinenDepartman"]) {
                        deleted["SilinenDepartman"] = {
                            name: this.$t("mail.mailGroupedDeletedDepartmentName"),
                            isDeleted: true,
                            mails: [],
                        };
                    }
                    deleted["SilinenDepartman"].mails.push(mail);
                } else {
                    const departmentId = mail.department._id;
                    const departmentName = this.getDepartmentName(mail);

                    if (!active[departmentId]) {
                        active[departmentId] = {
                            name: departmentName || this.$t("mail.mailUnknownDepartment"),
                            mails: [],
                        };
                    }
                    active[departmentId].mails.push(mail);
                }
            });

            return Object.assign({}, deleted, active);
        },
    },
    methods: {
        ...mapMutations({
            DELETE_MAIL_ADDRESS: "mail/DELETE_MAIL_ADDRESS",
            CHANGE_NEW_USER_MAIL_STATE: "mail/CHANGE_NEW_USER_MAIL_STATE",
            CHANGE_NEW_DEPARTMENT_MAIL_STATE: "mail/CHANGE_NEW_DEPARTMENT_MAIL_STATE",
        }),
        getSelectDepartmentList() {
            const departmentIds = Object.keys(this.groupedDepartmentMails).filter((id) => id !== "Bilinmeyen");
            return this.subjects.data.filter((x) => !departmentIds.includes(x._id));
        },
        getDepartmentName(value) {
            if (!!value.department && value.department._id) {
                const result = this.subjects.data.find((x) => x._id === value.department._id);
                if (!result) return "";
                return result.languageContent.tr.name;
            }
            return this.$t("mail.mailUnknownDepartment");
        },
        isAllowCreateDepartment() {
            const departmentGroups = Object.keys(this.groupedDepartmentMails).filter((id) => id !== "Bilinmeyen");
            return departmentGroups.length < this.subjects.total;
        },
        showModel(isDepartment = false) {
            this.isMailError = false;
            this.isMailExists = false;
            this.mailErrorMessage = "";
            this.emailName = "";

            if (!!isDepartment && this.isAllowCreateDepartment()) {
                this.CHANGE_NEW_DEPARTMENT_MAIL_STATE(true);
            } else if (!isDepartment) {
                this.CHANGE_NEW_USER_MAIL_STATE(true);
            }
        },
        mailDetail(isDepartment = false, selectedId = null) {
            if (!!isDepartment && !!selectedId.department && selectedId.department._id) {
                this.$router.push(`/integrations/mail/${selectedId.department._id}/department/detail/integration`);
            } else {
                this.$router.push(`/integrations/mail/${selectedId}/user/detail/integration`);
            }
        },
        handleCancel() {
            this.CHANGE_NEW_DEPARTMENT_MAIL_STATE(false);
            this.CHANGE_NEW_USER_MAIL_STATE(false);
            this.emailBtnEnabled = true;
            this.emailName = "";
            this.isMailError = false;
            this.isMailExists = false;
            this.mailErrorMessage = "";
            this.$message.destroy();
        },
        getUserEmailAddress() {
            console.log("this.allMailAddresses", this.allMailAddresses);
            const result = this.allMailAddresses.filter(
                (email) =>
                    email.type === "user" &&
                    !!email.user &&
                    email.user._id === this.user._id &&
                    !!email.isVerified &&
                    !email.isForward
            );
            if (result.length === 0) return [];
            return result;
        },
        getDepartmentEmailAddress() {
            const result = this.allMailAddresses.filter((email) => email.type === "department" && !!email.isVerified);
            if (!result) return [];
            return result;
        },
        createDomain() {
            this.$message.loading(this.$t("mail.mailDomainCreating"), 0);
            this.emailBtnEnabled = false;
            sendSocketMessage("email create_domain", {
                siteId: this.site._id,
                emailDomain: this.emailDomain,
            });
        },
        createDepartment() {
            if (this.emailName === "") {
                this.$message.error(this.$t("mail.mailNameRequired"));
                return;
            }
            for (let i = 0; i < this.emailName.length; i++) {
                if (
                    ["ç", "ğ", "ı", "ö", "ş", "ü", "Ç", "Ğ", "İ", "Ö", "Ş", "Ü", " ", ","].includes(
                        this.emailName.charAt(i)
                    )
                ) {
                    this.$message.error(this.$t("mail.mailInvalidLocalChars"));
                    return;
                }
            }
            this.$message.loading(this.$t("mail.mailEmailCreating"), 0);
            this.emailBtnEnabled = false;
            sendSocketMessage("email create_department_email", {
                siteId: this.site._id,
                identity: this.emailName + "@" + this.emailDomain,
                departmentId: this.selectedDepartmentId,
                emailDomain: this.emailDomain,
            });
        },

        createUserEmail() {
            if (this.emailName === "") {
                this.$message.error(this.$t("mail.mailNameRequired"));
                return;
            }
            for (let i = 0; i < this.emailName.length; i++) {
                if (
                    ["ç", "ğ", "ı", "ö", "ş", "ü", "Ç", "Ğ", "İ", "Ö", "Ş", "Ü", " ", ","].includes(
                        this.emailName.charAt(i)
                    )
                ) {
                    this.$message.error(this.$t("mail.mailInvalidLocalChars"));
                    return;
                }
            }
            this.$message.loading(this.$t("mail.mailEmailCreating"), 0);
            this.emailBtnEnabled = false;

            sendSocketMessage("email create_user_email", {
                siteId: this.site._id,
                identity: this.emailName + "@" + this.emailDomain,
                userId: this.user._id,
                emailDomain: this.emailDomain,
            });
        },
        deleteMailAddress(mail) {
            if (!this.isMailDeletePermission) {
                this.$message.error(this.$t("mail.senderNoPermissionForAction"));
                return;
            }

            this.$dialog
                .confirm(this.$t("mail.mailDeleteConfirmIntegration"), {
                    okText: this.$t("common.yes"),
                    cancelText: this.$t("common.no"),
                    customClass: "supsis-dialog",
                })
                .then(() => {
                    sendSocketMessage(
                        mail.type === "user" ? "email delete_user_email" : "email delete_department_email",
                        {
                            siteId: this.site._id,
                            mailAddressId: mail._id,
                            identity: mail.mail,
                        }
                    );
                    this.DELETE_MAIL_ADDRESS(mail._id);
                    this.$message.success(this.$t("mail.senderMailDeletedSuccess"));
                })
                .catch(() => {});
        },
        checkMailValidity() {
            if (this.emailName.length > 0) {
                const isValid = /^[a-zA-Z0-9\-.+]+$/.test(this.emailName);
                const hasTurkishChars = /[çğıöşüÇĞİÖŞÜ]/.test(this.emailName);
                const hasSpace = /\s/.test(this.emailName);

                if (!isValid) {
                    this.isMailError = true;
                    if (hasTurkishChars) {
                        this.mailErrorMessage = this.$t("mail.mailErrorNoTurkishChars");
                    } else if (hasSpace) {
                        this.mailErrorMessage = this.$t("mail.mailErrorNoSpacesInLocal");
                    } else {
                        this.mailErrorMessage = this.$t("mail.mailErrorInvalidLocal");
                    }
                    return;
                }

                this.checkMailExists();
            } else {
                this.isMailError = false;
                this.mailErrorMessage = "";
                this.isMailExists = false;
            }
        },

        checkMailExists() {
            const fullMail = this.emailName + "@" + this.emailDomain;

            const exists = this.allMailAddresses.some((mail) => mail.mail.toLowerCase() === fullMail.toLowerCase());

            if (exists) {
                this.isMailError = true;
                this.isMailExists = true;
                this.mailErrorMessage = this.$t("mail.senderMailAlreadyRegistered");
            } else {
                this.isMailError = false;
                this.isMailExists = false;
                this.mailErrorMessage = "";
            }
        },
        handleKeydown(e) {
            if ("Escape" === e.key || "Esc" === e.key) {
                this.CHANGE_NEW_USER_MAIL_STATE(false);
                this.CHANGE_NEW_DEPARTMENT_MAIL_STATE(false);
                this.emailBtnEnabled = true;
                this.emailName = "";
            }
        },
    },
    async mounted() {
        this.stream = socketMessageStream.subscribe(({ cmd }) => {
            if (cmd === "new_mail_address") {
                this.$message.destroy();
                this.$message.success(this.$t("mail.mailNewAddressCreated"));
                this.emailBtnEnabled = true;
                this.CHANGE_NEW_USER_MAIL_STATE(false);
                this.CHANGE_NEW_DEPARTMENT_MAIL_STATE(false);
            }
        });
        window.addEventListener("keydown", this.handleKeydown);
    },
    beforeMount() {
        if (!this.isMailUpdatePermission) {
            return this.$router.push("/denied");
        }
    },
    beforeDestroy() {
        if (this.stream) this.stream.unsubscribe();
        window.removeEventListener("keydown", this.handleKeydown);
    },
    watch: {
        isNewDomain(val) {
            if (!!this.domainIsCreated) {
                this.$message.destroy();
                this.$message.success(this.$t("mail.mailDomainCreatedSuccess"));
                this.emailBtnEnabled = true;
            } else {
                this.$message.destroy();
                this.$message.error(this.$t("mail.mailGenericError"));
                this.emailBtnEnabled = true;
            }
        },
        emailName() {
            this.checkMailValidity();
        },
    },
};
</script>

<style lang="scss">
#mail-integration {
    background: #f8f9fa;
    height: 100%;
    padding: 30px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans",
        "Helvetica Neue", sans-serif;

    .email-service-status {
        margin-bottom: 30px;

        .service-card {
            background: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
            overflow: hidden;

            .service-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 15px 20px;
                border-bottom: 1px solid #f0f0f0;

                .service-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }

                .service-status {
                    font-size: 14px;
                    font-weight: 500;
                    color: #f56c6c;
                    background: rgba(245, 108, 108, 0.1);
                    padding: 5px 12px;
                    border-radius: 20px;

                    &.status-active {
                        color: #67c23a;
                        background: rgba(103, 194, 58, 0.1);
                    }
                }
            }

            .service-content {
                display: flex;
                align-items: center;
                padding: 20px;

                .provider-logo {
                    width: 50px;
                    height: 50px;
                    overflow: hidden;
                    border-radius: 6px;
                    margin-right: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f5f5f5;

                    img {
                        max-width: 40px;
                        max-height: 40px;
                    }
                }

                .provider-info {
                    flex: 1;

                    .provider-name {
                        font-size: 16px;
                        font-weight: 500;
                        color: #333;
                        margin-bottom: 5px;
                    }

                    .domain-name {
                        font-size: 14px;
                        color: #666;
                    }
                }

                .action-area {
                    .domain-connected {
                        display: flex;
                        align-items: center;
                        color: #67c23a;
                        font-size: 14px;
                        font-weight: 500;

                        .check-icon {
                            margin-right: 5px;
                            font-size: 18px;
                        }
                    }
                }
            }
        }
    }

    .mail-dashboard {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        overflow: hidden;

        .dashboard-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 15px 20px;
            border-bottom: 1px solid #f0f0f0;

            .dashboard-title {
                font-size: 16px;
                font-weight: 600;
                color: #333;
            }

            .dashboard-tabs {
                display: flex;
                align-items: center;

                .tab-count {
                    font-size: 12px;
                    background: #f5f5f5;
                    color: #666;
                    padding: 2px 8px;
                    border-radius: 10px;
                }
            }
        }

        .mail-section {
            padding: 20px;
            border-bottom: 1px solid #f5f5f5;

            &:last-child {
                border-bottom: none;
            }

            .section-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 15px;

                .section-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;
                    display: flex;
                    align-items: center;

                    .section-icon {
                        font-size: 18px;
                        margin-right: 8px;
                        color: #22272d;
                    }
                }
            }

            .mail-groups {
                display: flex;
                flex-direction: column;
                gap: 15px;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: #f9f9f9;
                border: 1px dashed #ddd;
                border-radius: 6px;
                padding: 30px;
                margin: 15px 0;

                .empty-icon {
                    font-size: 40px;
                    color: #ccc;
                    margin-bottom: 10px;
                }

                .empty-text {
                    font-size: 16px;
                    font-weight: 500;
                    color: #666;
                    margin-bottom: 5px;
                }

                .empty-description {
                    font-size: 14px;
                    color: #999;
                }
            }

            .mail-group-container {
                margin-bottom: 20px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            .mail-group {
                background: #fff;
                border: 1px solid #eee;
                border-radius: 6px;
                overflow: hidden;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

                &.deleted-group {
                    border-color: #f5c6c6;

                    .group-header {
                        background: #fff5f5;

                        .group-title {
                            color: #c0392b;

                            .user-icon,
                            .department-icon {
                                color: #c0392b;
                            }
                        }
                    }
                }

                .group-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 15px;
                    background: #f9fafc;
                    border-bottom: 1px solid #f0f0f0;

                    .group-title {
                        display: flex;
                        align-items: center;
                        font-size: 14px;
                        font-weight: 600;
                        color: #333;

                        .user-icon,
                        .department-icon {
                            font-size: 18px;
                            margin-right: 8px;
                            color: #22272d;
                        }
                    }

                    .group-badges {
                        display: flex;
                        gap: 5px;

                        .badge {
                            font-size: 12px;
                            padding: 2px 8px;
                            border-radius: 10px;

                            &.mail-count {
                                background: rgba(34, 39, 45, 0.1);
                                color: #22272d;
                            }
                        }
                    }
                }

                .mail-items {
                    .mail-item {
                        display: flex;
                        align-items: center;
                        padding: 12px 15px;
                        transition: all 0.25s ease;
                        cursor: pointer;
                        border-bottom: 1px solid #f5f5f5;
                        position: relative;

                        &:last-child {
                            border-bottom: none;
                        }

                        &:hover {
                            background-color: #f8f9fa;

                            .mail-actions {
                                opacity: 1;
                            }
                        }

                        .mail-icon {
                            margin-right: 12px;

                            svg {
                                width: 24px;
                                height: 24px;
                                color: #22272d;
                            }
                        }

                        .mail-info {
                            flex: 1;
                            padding: 5px 0;
                            cursor: pointer;

                            .mail-address {
                                font-size: 14px;
                                font-weight: 500;
                                color: #333;
                                margin-bottom: 4px;
                            }

                            .mail-meta {
                                display: flex;
                                align-items: center;
                                gap: 10px;

                                .mail-status {
                                    font-size: 12px;
                                    font-weight: 500;
                                    color: #f56c6c;

                                    &.verified {
                                        color: #67c23a;
                                    }
                                }

                                .mail-type {
                                    font-size: 12px;
                                    color: #999;
                                }
                            }
                        }

                        .mail-actions {
                            opacity: 0.5;
                            transition: opacity 0.2s;
                            display: flex;
                            align-items: center;

                            .delete-button {
                                width: 28px;
                                height: 28px;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                transition: all 0.2s;

                                .material-icons {
                                    font-size: 18px;
                                    color: #999;
                                    transition: all 0.2s;
                                }

                                &:hover {
                                    background-color: rgba(245, 108, 108, 0.1);

                                    .material-icons {
                                        color: #f56c6c;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .setup-required {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        padding: 40px;
        margin-top: 30px;

        .setup-content {
            text-align: center;
            max-width: 400px;

            .setup-icon {
                font-size: 60px;
                color: #ccc;
                margin-bottom: 20px;
            }

            .setup-title {
                font-size: 18px;
                font-weight: 600;
                color: #333;
                margin-bottom: 10px;
            }

            .setup-description {
                font-size: 14px;
                color: #666;
                line-height: 1.5;
            }
        }
    }
}

.mail-create-modal {
    .modal-body {
        padding: 0;

        .modal-custom-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 30px 20px 25px;
            background-color: #fff;
            border-bottom: 1px solid #f0f0f0;
            text-align: center;

            .header-icon {
                width: 50px;
                height: 50px;
                border-radius: 50%;
                background-color: #f5f5f5;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 15px;

                .material-icons {
                    font-size: 24px;
                    color: #333;
                }
            }

            .header-title {
                font-size: 20px;
                font-weight: 600;
                color: #333;
                margin: 0;
            }
        }

        .modal-custom-content {
            padding: 25px 30px;

            .form-item {
                margin-bottom: 25px;

                label {
                    display: block;
                    font-size: 15px;
                    font-weight: 500;
                    color: #333;
                    margin-bottom: 10px;
                }

                .ant-select {
                    width: 100%;

                    .ant-select-selection {
                        height: 40px;
                        border-radius: 4px;

                        .ant-select-selection__rendered {
                            line-height: 38px;
                            margin-left: 11px;
                        }
                    }
                }
            }

            .mail-field-container {
                .mail-input-container {
                    display: flex;
                    align-items: center;
                    border: 1px solid #d9d9d9;
                    border-radius: 4px;
                    overflow: hidden;
                    height: 40px;

                    &.input-error {
                        border-color: #f56c6c;
                    }

                    .mail-name-input {
                        flex: 1;
                        border: none;
                        border-radius: 0;
                        height: 40px;
                        font-size: 14px;

                        &:focus {
                            box-shadow: none;
                        }
                    }

                    .mail-domain {
                        padding: 0 15px;
                        background: #f5f5f5;
                        border-left: 1px solid #d9d9d9;
                        color: #666;
                        font-size: 14px;
                        height: 40px;
                        line-height: 40px;
                    }
                }

                .input-error-message {
                    margin-top: 8px;
                    font-size: 13px;
                    color: #f56c6c;
                    display: flex;
                    align-items: center;

                    &::before {
                        content: "!";
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 16px;
                        height: 16px;
                        background-color: #f56c6c;
                        color: white;
                        font-size: 12px;
                        font-weight: bold;
                        border-radius: 50%;
                        margin-right: 6px;
                    }
                }

                .input-helper {
                    margin-top: 8px;
                    font-size: 13px;
                    color: #999;
                }
            }

            .form-notice {
                display: flex;
                align-items: flex-start;
                background: #fffbf2;
                border: 1px solid #ffe7ba;
                border-radius: 4px;
                padding: 12px 15px;
                margin: 0;

                .notice-icon {
                    font-size: 20px;
                    color: #faad14;
                    margin-right: 12px;
                    margin-top: 2px;
                }

                .notice-text {
                    font-size: 13px;
                    color: #666;
                    line-height: 1.5;
                }
            }
        }

        .modal-custom-footer {
            display: flex;
            justify-content: flex-end;
            padding: 20px 30px;
            background: #fff;
            border-top: 1px solid #f0f0f0;
            gap: 12px;

            .cancel-btn {
                border: 1px solid #d9d9d9;
                color: #666;
                background: #fff;

                &:hover {
                    border-color: #999;
                    color: #333;
                }
            }

            .create-btn {
                background: #22272d;
                border-color: #22272d;

                &:hover {
                    background: #333;
                    border-color: #333;
                }
            }
        }
    }

    .custom-modal-body {
        padding: 0 !important;
        border-radius: 6px;
        overflow: hidden;
    }
}

/* Global dialog styles */
.supsis-dialog {
    .dg-content-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;

        .dg-btn--cancel {
            color: #22272d;
            background-color: #fefefe;
            border-color: #22272d;
        }

        .dg-btn--ok {
            color: #fff;
            background-color: #22272d;
            border-color: #22272d;
        }
    }
}
</style>
